import { Component } from '@theme/component';
import {
  onDocumentLoaded,
  changeMetaThemeColor,
  setHeaderMenuStyle,
  lockScroll,
  unlockScroll,
  updateAllHeaderCustomProperties,
} from '@theme/utilities';
import { hideZendeskLauncher, restoreZendeskLauncher } from '@theme/zendesk-launcher';

const ZENDESK_HIDE_SOURCE = 'mobile-nav';

const MENU_TYPES = {
  DESKTOP: 'desktop',
  MOBILE: 'mobile',
};

/**
 * Hysteresis thresholds for the header scroll state. Entering "scrolled"
 * hides the mobile store-availability bar (shrinking the header), so the
 * enter threshold sits past the bar's height to avoid a show/hide flicker
 * loop right at the boundary.
 */
const SCROLLED_ENTER_Y = 60;
const SCROLLED_EXIT_Y = 8;

/** Desktop header row (matches the lg breakpoint the header markup uses). */
const DESKTOP_QUERY = window.matchMedia('(min-width: 1024px)');

/** Each menu item's own end padding: hiding less than this cuts no text. */
const NAV_SLACK = 8;

class HeaderComponent extends Component {
  currentOpenMenuType = null;
  scrollStateHandler = null;
  headerResizeObserver = null;
  storeTriggerObserver = null;

  connectedCallback() {
    super.connectedCallback();

    const navTriggers = this.querySelectorAll('.js-nav-trigger');
    const searchDrawerTrigger = this.querySelector('.js-search-trigger');
    const backdrop = this.querySelector('.js-nav-backdrop');

    const mobileNavTrigger = this.querySelector('.js-mobile-nav-trigger');
    const mobileMenuItems = this.querySelectorAll('.js-mobile-menu-item');
    const mobileSubmenuBackBtns = this.querySelectorAll(
      '.js-mobile-submenu-back-btn',
    );

    navTriggers.forEach((navTrigger) => {
      navTrigger.addEventListener('mouseenter', (e) =>
        this.handleNavTriggerMouseEnter(e),
      );

      // A top-level item with a dropdown opens it on click instead of following
      // its link (and instead of the native <summary> toggle, which would close
      // a dropdown hover already opened). Items without a dropdown navigate.
      if (navTrigger.dataset.hasSubmenu !== 'true') return;
      navTrigger.querySelector(':scope > summary')?.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleNavTriggerMouseEnter({ target: navTrigger });
      });
    });

    mobileNavTrigger?.addEventListener('click', (e) => {
      if (mobileNavTrigger.classList.contains('--is-open')) {
        this.closeCurrentNav();
      } else {
        this.openMobileNav(e);
      }
    });

    mobileMenuItems.forEach((mobileMenuItem) => {
      mobileMenuItem.addEventListener('click', (e) =>
        this.openMobileSubmenu(e),
      );
    });

    backdrop?.addEventListener('mouseenter', () => this.closeCurrentNav());

    backdrop?.addEventListener('click', () => {
      this.closeSearchUI();
      this.closeStoreBar();
      this.closeCurrentNav();
      this.closeBackdrop();
    });

    mobileSubmenuBackBtns.forEach((mobileSubmenuBackBtn) => {
      mobileSubmenuBackBtn.addEventListener('click', (e) =>
        this.mobileSubmenuBack(e),
      );
    });

    searchDrawerTrigger?.addEventListener('click', (e) =>
      this.toggleSearchDrawer(e),
    );

    const storeBarTrigger = this.querySelector('.js-store-bar-trigger');
    storeBarTrigger?.addEventListener('click', () => this.toggleStoreBar());

    this.initScrollState();
  }

  /**
   * Tracks whether the page is scrolled and mirrors it as an `is-scrolled`
   * class on this element. On mobile, CSS keys off it: at scroll top the
   * store-availability bar is visible and the location icon hidden; once
   * scrolled the bar collapses and the location icon (which toggles the bar)
   * appears. Each state change resets any manual toggle so the CSS defaults
   * for the new state apply.
   */
  initScrollState() {
    let ticking = false;

    const update = () => {
      ticking = false;

      // While page scroll is locked (drawer/modal open) the body is
      // position:fixed and window.scrollY reads 0 — ignore to avoid a
      // false flip back to the top state.
      if (document.documentElement.hasAttribute('scroll-lock')) return;

      const isScrolled = this.classList.contains('is-scrolled');
      const scrolled = isScrolled
        ? window.scrollY > SCROLLED_EXIT_Y
        : window.scrollY > SCROLLED_ENTER_Y;

      if (scrolled === isScrolled) return;

      this.classList.toggle('is-scrolled', scrolled);
      this.closeStoreBar();
    };

    this.scrollStateHandler = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };

    window.addEventListener('scroll', this.scrollStateHandler, {
      passive: true,
    });
    update();

    // The header's height is no longer static: the mobile store-availability
    // bar slides open/closed with the scroll state (and via the location-icon
    // toggle), but --header-height is only measured once by the inline script
    // in layout/theme.liquid. Keep the header CSS variables live so elements
    // pinned with top: var(--header-height) (e.g. the collection toolbar)
    // track the real header edge — including every frame of the transition.
    this.headerResizeObserver = new ResizeObserver(() => {
      this.updateHeaderLayout();
      updateAllHeaderCustomProperties();
    });
    this.headerResizeObserver.observe(this);

    // The pill labels change without resizing the header (store picked,
    // cleared, date changed), so re-check the layout on those too.
    const storeTrigger = this.querySelector('.store-availability-desktop-trigger');
    if (storeTrigger) {
      this.storeTriggerObserver = new MutationObserver(() => this.updateHeaderLayout());
      this.storeTriggerObserver.observe(storeTrigger, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
      });
    }

    const nav = this.querySelector('.header__nav-slot > .js-mobile-nav');
    nav?.addEventListener('scroll', () => this.updateNavScroll(), { passive: true });

    // The web font loading widens the labels without any of the above firing.
    document.fonts?.ready.then(() => this.updateHeaderLayout());
  }

  /**
   * EA-4425: desktop header layout. The logo stays centered while the menu and
   * the actions each fit their half; otherwise [data-logo-start] moves the logo
   * to the start so the actions keep their full width and the menu gets the
   * rest. Measured in the centered layout and set again in the same frame, so it
   * never flickers or flip-flops.
   */
  updateHeaderLayout() {
    this.removeAttribute('data-logo-start');

    const nav = this.querySelector('.header__nav-slot > .js-mobile-nav');
    const actions = this.querySelector('.header__actions-slot');
    if (DESKTOP_QUERY.matches && nav && actions) {
      const menuCut = nav.scrollWidth - nav.clientWidth > NAV_SLACK;
      const trigger = actions.querySelector('.store-availability-desktop-trigger');
      const labelCut = [...actions.querySelectorAll('.store-availability-trigger__pill-text')].some(
        (label) => label.scrollWidth > label.clientWidth,
      );
      // The actions are right-aligned, so extra content spills out of the
      // column's left edge (into the logo) - scrollWidth doesn't count that side.
      const actionsSpill = Boolean(trigger) && trigger.getBoundingClientRect().left < actions.getBoundingClientRect().left - 1;
      if (menuCut || labelCut || actionsSpill) this.setAttribute('data-logo-start', '');
    }

    this.updateNavScroll();
  }

  /**
   * EA-4425: the desktop menu stays on one line and scrolls sideways when it
   * runs out of room; shows the fade on each side that has more items.
   */
  updateNavScroll() {
    const slot = this.querySelector('.header__nav-slot');
    const nav = slot?.querySelector(':scope > .js-mobile-nav');
    if (!nav) return;

    const hidden = DESKTOP_QUERY.matches ? nav.scrollWidth - nav.clientWidth : 0;
    slot.toggleAttribute('data-more-start', hidden > NAV_SLACK && nav.scrollLeft > 1);
    slot.toggleAttribute('data-more-end', hidden > NAV_SLACK && nav.scrollLeft < hidden - 1);
  }

  /**
   * Toggles the mobile store-availability bar. The bar cannot coexist with the
   * mobile menu or the search drawer, so opening it closes both.
   */
  toggleStoreBar() {
    const storeBar = this.querySelector('.js-store-bar');
    if (!storeBar) return;

    const willOpen = !storeBar.classList.contains('--is-open');

    if (!willOpen) {
      this.closeStoreBar();
      return;
    }

    this.closeCurrentNav();
    this.closeSearchUI();
    this.closeBackdrop();

    storeBar.classList.add('--is-open');
    const storeBarTrigger = this.querySelector('.js-store-bar-trigger');
    storeBarTrigger?.classList.add('--is-open');
    storeBarTrigger?.setAttribute('aria-expanded', 'true');
  }

  closeStoreBar() {
    const storeBar = this.querySelector('.js-store-bar');
    const storeBarTrigger = this.querySelector('.js-store-bar-trigger');
    storeBar?.classList.remove('--is-open');
    storeBarTrigger?.classList.remove('--is-open');
    storeBarTrigger?.setAttribute('aria-expanded', 'false');
  }

  /**
   * Closes the search drawer UI only (classes). Does not change backdrop;
   * callers coordinate with openBackdrop / closeBackdrop.
   */
  closeSearchUI() {
    const searchDrawer = this.querySelector('.js-search-drawer');
    const searchDrawerTrigger = this.querySelector('.js-search-trigger');
    searchDrawer?.classList.remove('--is-open', 'tw---is-open');
    searchDrawerTrigger?.classList.remove('--is-open');
  }

  toggleSearchDrawer(event) {
    const searchDrawerTrigger = event.currentTarget;
    const searchDrawer = this.querySelector('.js-search-drawer');
    if (!searchDrawer) return;

    const willOpen = !searchDrawer.classList.contains('--is-open');

    if (willOpen) {
      this.closeCurrentNav();
      this.closeStoreBar();
    }

    searchDrawer.classList.toggle('--is-open');
    searchDrawer.classList.toggle('tw---is-open');

    searchDrawerTrigger.classList.toggle('--is-open');

    if (searchDrawer.classList.contains('--is-open')) {
      this.openBackdrop();
    } else {
      this.closeBackdrop();
    }
  }

  /**
   * Closes the header search drawer (used by React predictive search close control).
   * @param {Event} [event]
   */
  closeSearchDrawer(event) {
    event?.preventDefault();
    this.closeSearchUI();
    this.closeBackdrop();
  }

  disconnectedCallback() {
    if (this.scrollStateHandler) {
      window.removeEventListener('scroll', this.scrollStateHandler);
      this.scrollStateHandler = null;
    }
    this.headerResizeObserver?.disconnect();
    this.headerResizeObserver = null;
    this.storeTriggerObserver?.disconnect();
    this.storeTriggerObserver = null;
    unlockScroll();
    super.disconnectedCallback();
  }

  openMobileNav(event) {
    const mobileNav = this.querySelector('.js-mobile-nav');
    if (!mobileNav) return;

    this.closeSearchUI();
    this.closeStoreBar();

    event.currentTarget.classList.add('--is-open');
    mobileNav.classList.add('--is-open', 'tw---is-open');

    const topSpacing = Math.min(0, window.scrollY - 33) + 33;
    mobileNav.style.setProperty('--height-exclude-top-bar', `${topSpacing}px`);
    this.currentOpenMenuType = MENU_TYPES.MOBILE;
    hideZendeskLauncher(ZENDESK_HIDE_SOURCE);
    this.openBackdrop();
  }

  openMobileSubmenu(event) {
    const submenu = event.currentTarget;
    if (!submenu) return;

    const { hasSubmenu, triggerId } = submenu.dataset;
    const mobileSubmenu = this.querySelector(`#mobile-submenu-${triggerId}`);
    if (hasSubmenu === 'true') {
      mobileSubmenu.classList.add('--is-open', 'tw---is-open');
      // mobileSubmenu?.scrollIntoView({ behavior: 'instant' });
    } else {
      mobileSubmenu.classList.remove('--is-open', 'tw---is-open');
    }
  }

  mobileSubmenuBack(event) {
    const submenu = event.target;
    if (!submenu) return;

    const mobileSubNav = submenu.closest('.js-mobile-submenu');
    mobileSubNav.classList.remove('--is-open', 'tw---is-open');
  }

  handleNavTriggerMouseEnter(event) {
    const details = event.target;
    if (!details) return;

    const { hasSubmenu } = details.dataset;
    if (hasSubmenu === 'true') {
      this.closeSearchUI();
      // Close any other open submenu first so only the selected one stays open. The
      // keyboard/click path (summary click → here) can otherwise open a second
      // dropdown on top of one already opened by hover, leaving two open at once.
      this.querySelectorAll('.js-nav-trigger[open]').forEach((nav) => {
        if (nav !== details) nav.open = false;
      });
      details.open = true;
      this.currentOpenMenuType = MENU_TYPES.DESKTOP;
      this.openBackdrop();
    } else {
      const openNav = this.querySelector('.js-nav-trigger[open]');
      this.closeSearchUI();
      if (openNav) openNav.open = false;
      this.closeBackdrop();
    }
  }

  closeCurrentNav() {
    const desktopNav = this.querySelector('.js-nav-trigger[open]');
    const mobileNav = this.querySelector('.js-mobile-nav.--is-open');

    const openNav =
      this.currentOpenMenuType === MENU_TYPES.DESKTOP ? desktopNav : mobileNav;

    if (!openNav) return;

    if (this.currentOpenMenuType === MENU_TYPES.DESKTOP) {
      desktopNav.open = false;
    } else {
      const mobileNavTrigger = this.querySelector('.js-mobile-nav-trigger');

      mobileNav.classList.remove('--is-open', 'tw---is-open');
      mobileNavTrigger.classList.remove('--is-open');
      restoreZendeskLauncher(ZENDESK_HIDE_SOURCE);
      const mobileSubmenusBackBtn = document.querySelector(
        '.js-mobile-submenu.--is-open > .js-mobile-submenu-back-btn',
      );
      this.mobileSubmenuBack({ target: mobileSubmenusBackBtn });
    }

    this.closeBackdrop();
  }

  openBackdrop() {
    const backdrop = this.querySelector('.js-nav-backdrop');
    if (!backdrop) return;

    const wasOpen = backdrop.classList.contains('open');
    backdrop.classList.add('open');
    if (!wasOpen) lockScroll();
  }

  closeBackdrop() {
    const backdrop = this.querySelector('.js-nav-backdrop');
    if (!backdrop) return;

    const wasOpen = backdrop.classList.contains('open');
    backdrop.classList.remove('open');
    this.currentOpenMenuType = null;
    if (wasOpen) unlockScroll();
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}
